import { useMemo, useState } from 'react';
import { useCustomization } from '../../contexts/CustomizationContext';
import { useTheme } from '../../contexts/ThemeContext';
import './CustomizationPanel.scss';

const fonts = [
  { name: 'Editorial', value: "'EB Garamond', serif", sample: 'Elegant portfolio voice' },
  { name: 'Inter', value: "'Inter', system-ui, sans-serif", sample: 'Clean product feel' },
  { name: 'Roboto', value: "'Roboto', system-ui, sans-serif", sample: 'Balanced and familiar' },
  { name: 'Open Sans', value: "'Open Sans', system-ui, sans-serif", sample: 'Soft readable UI' },
  { name: 'Lato', value: "'Lato', system-ui, sans-serif", sample: 'Warm professional tone' },
  { name: 'Montserrat', value: "'Montserrat', system-ui, sans-serif", sample: 'Confident headings' },
  { name: 'Poppins', value: "'Poppins', system-ui, sans-serif", sample: 'Rounded modern style' },
  { name: 'Playfair', value: "'Playfair Display', Georgia, serif", sample: 'Premium editorial look' }
];

const colorControls = [
  { key: 'primaryColor', label: 'Primary', note: 'Buttons and highlights' },
  { key: 'primaryColor1', label: 'Accent', note: 'Gradients and emphasis' },
  { key: 'primaryColor2', label: 'Secondary', note: 'Charts and badges' },
  { key: 'primaryColor3', label: 'Soft', note: 'Hover states' },
  { key: 'primaryColor4', label: 'Tint', note: 'Subtle surfaces' }
];

const stylePresets = [
  {
    name: 'Fawaz Classic',
    description: 'Deep teal with a focused portfolio feel.',
    theme: 'light',
    values: {
      fontFamily: "'EB Garamond', serif",
      primaryColor: '#014d4e',
      primaryColor1: '#069093',
      primaryColor2: '#3b82f6',
      primaryColor3: '#60a5fa',
      primaryColor4: '#93c5fd',
      surfaceStyle: 'glass',
      visualDepth: 'soft'
    }
  },
  {
    name: 'Studio Ink',
    description: 'Sharp monochrome base with electric blue detail.',
    theme: 'dark',
    values: {
      fontFamily: "'Inter', system-ui, sans-serif",
      primaryColor: '#38bdf8',
      primaryColor1: '#22d3ee',
      primaryColor2: '#a78bfa',
      primaryColor3: '#818cf8',
      primaryColor4: '#c4b5fd',
      surfaceStyle: 'solid',
      visualDepth: 'soft'
    }
  },
  {
    name: 'Mint Lab',
    description: 'Fresh green accents for a calm technical profile.',
    theme: 'light',
    values: {
      fontFamily: "'Lato', system-ui, sans-serif",
      primaryColor: '#047857',
      primaryColor1: '#10b981',
      primaryColor2: '#0f766e',
      primaryColor3: '#34d399',
      primaryColor4: '#a7f3d0',
      surfaceStyle: 'glass',
      visualDepth: 'floating'
    }
  },
  {
    name: 'Warm Signal',
    description: 'Confident amber and red for a stronger call to action.',
    theme: 'light',
    values: {
      fontFamily: "'Poppins', system-ui, sans-serif",
      primaryColor: '#b45309',
      primaryColor1: '#ea580c',
      primaryColor2: '#dc2626',
      primaryColor3: '#f97316',
      primaryColor4: '#fed7aa',
      surfaceStyle: 'outlined',
      visualDepth: 'soft'
    }
  }
];

const radiusOptions = [
  { name: 'Crisp', value: '4px', description: 'Sharper product edges' },
  { name: 'Soft', value: '12px', description: 'Balanced card corners' },
  { name: 'Round', value: '18px', description: 'Friendlier surfaces' },
  { name: 'Pill', value: '28px', description: 'Highly rounded controls' }
];

const densityOptions = [
  { name: 'Compact', value: 'compact', spacing: '0.75rem', description: 'Tighter sections' },
  { name: 'Balanced', value: 'balanced', spacing: '1rem', description: 'Default rhythm' },
  { name: 'Spacious', value: 'spacious', spacing: '1.25rem', description: 'More breathing room' }
];

const fontScaleOptions = [
  { name: 'Compact', value: 'compact', description: 'Slightly smaller text' },
  { name: 'Normal', value: 'normal', description: 'Default reading size' },
  { name: 'Large', value: 'large', description: 'Bigger interface text' }
];

const surfaceOptions = [
  { name: 'Solid', value: 'solid', description: 'Clean filled panels' },
  { name: 'Glass', value: 'glass', description: 'Soft translucent layers' },
  { name: 'Outlined', value: 'outlined', description: 'Light framed sections' }
];

const depthOptions = [
  { name: 'Flat', value: 'flat', description: 'Minimal shadows' },
  { name: 'Soft', value: 'soft', description: 'Gentle elevation' },
  { name: 'Floating', value: 'floating', description: 'More dramatic lift' }
];

const widthOptions = [
  { name: 'Focused', value: 'focused', description: 'Narrower reading width' },
  { name: 'Balanced', value: 'balanced', description: 'Current portfolio width' },
  { name: 'Wide', value: 'wide', description: 'Roomier project grids' }
];

const motionOptions = [
  { name: 'Brisk', value: 'brisk', speed: '0.18s', description: 'Snappy transitions' },
  { name: 'Balanced', value: 'balanced', speed: '0.3s', description: 'Natural movement' },
  { name: 'Cinematic', value: 'cinematic', speed: '0.55s', description: 'Slower reveals' },
  { name: 'Reduced', value: 'reduced', speed: '0.01s', description: 'Minimal motion' }
];

const cursorOptions = [
  { name: 'Accent', value: 'accent', description: 'Branded cursor glow' },
  { name: 'Minimal', value: 'minimal', description: 'Precise native pointer' },
  { name: 'Native', value: 'native', description: 'Browser default' }
];

const contrastOptions = [
  { name: 'Normal', value: 'normal', description: 'Default contrast' },
  { name: 'High', value: 'high', description: 'Stronger text and borders' }
];

const Icon = ({ type }) => {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true'
  };

  switch (type) {
    case 'palette':
      return (
        <svg {...common}>
          <circle cx="13.5" cy="6.5" r="1.5" fill="currentColor" />
          <circle cx="17.5" cy="10.5" r="1.5" fill="currentColor" />
          <circle cx="8.5" cy="7.5" r="1.5" fill="currentColor" />
          <circle cx="6.5" cy="12.5" r="1.5" fill="currentColor" />
          <path d="M12 2C6.5 2 2 6.2 2 11.4C2 16.9 6.5 21 12 21h1.2c1 0 1.7-.8 1.7-1.7c0-.5-.2-.9-.5-1.2c-.3-.3-.5-.7-.5-1.2c0-1 .8-1.7 1.7-1.7H17c2.8 0 5-2.2 5-5C22 5.7 17.5 2 12 2Z" />
        </svg>
      );
    case 'type':
      return (
        <svg {...common}>
          <path d="M4 7V4h16v3" />
          <path d="M9 20h6" />
          <path d="M12 4v16" />
        </svg>
      );
    case 'layout':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M3 10h18" />
          <path d="M9 10v10" />
        </svg>
      );
    case 'motion':
      return (
        <svg {...common}>
          <path d="M4 12h7" />
          <path d="M4 6h13" />
          <path d="M4 18h9" />
          <path d="m17 13 4-4-4-4" />
        </svg>
      );
    case 'reset':
      return (
        <svg {...common}>
          <path d="M3 12a9 9 0 0 1 15.4-6.4L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-15.4 6.4L3 16" />
          <path d="M3 21v-5h5" />
        </svg>
      );
    case 'spark':
      return (
        <svg {...common}>
          <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2Z" />
          <path d="M19 15l.9 2.6L22 18.5l-2.1.9L19 22l-.9-2.6L16 18.5l2.1-.9L19 15Z" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3" />
          <path d="M12 19v3" />
          <path d="M4.2 4.2l2.1 2.1" />
          <path d="M17.7 17.7l2.1 2.1" />
          <path d="M2 12h3" />
          <path d="M19 12h3" />
          <path d="M4.2 19.8l2.1-2.1" />
          <path d="M17.7 6.3l2.1-2.1" />
        </svg>
      );
  }
};

const OptionGroup = ({ label, options, value, onChange }) => (
  <div className="custom-panel__control">
    <div className="custom-panel__control-label">{label}</div>
    <div className="custom-panel__choice-grid">
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          className={`custom-panel__choice ${value === option.value ? 'is-active' : ''}`}
          onClick={() => onChange(option)}
          aria-pressed={value === option.value}
        >
          <span className="custom-panel__choice-name">{option.name}</span>
          <span className="custom-panel__choice-description">{option.description}</span>
        </button>
      ))}
    </div>
  </div>
);

const SectionTitle = ({ icon, title, meta }) => (
  <div className="custom-panel__section-heading">
    <span className="custom-panel__section-icon">
      <Icon type={icon} />
    </span>
    <div>
      <h3>{title}</h3>
      {meta && <p>{meta}</p>}
    </div>
  </div>
);

const CustomizationPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const { customization, updateCustomization, updateCustomizations, resetCustomization } = useCustomization();

  const activePreset = useMemo(
    () => stylePresets.find((preset) => (
      Object.entries(preset.values).every(([key, value]) => customization[key] === value)
    )),
    [customization]
  );

  const selectedFont = fonts.find((font) => font.value === customization.fontFamily) || fonts[0];

  const applyPreset = (preset) => {
    updateCustomizations(preset.values);
    if (preset.theme) {
      setTheme(preset.theme);
    }
  };

  const handleDensityChange = (option) => {
    updateCustomizations({
      density: option.value,
      spacing: option.spacing
    });
  };

  const handleMotionChange = (option) => {
    updateCustomizations({
      motionMode: option.value,
      animationSpeed: option.speed
    });
  };

  return (
    <section className="custom-panel" id="customize" aria-labelledby="custom-panel-heading">
      <div className="custom-panel__container">
        <button
          type="button"
          className={`custom-panel__trigger ${isOpen ? 'is-open' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls="custom-panel-content"
          id="custom-panel-heading"
        >
          <span className="custom-panel__trigger-main">
            <span className="custom-panel__trigger-icon">
              <Icon type="spark" />
            </span>
            <span className="custom-panel__trigger-copy">
              <span className="custom-panel__eyebrow">Make it yours</span>
              <span className="custom-panel__title">Customize portfolio experience</span>
              <span className="custom-panel__subtitle">
                {activePreset?.name || selectedFont.name} · {theme} · {customization.density}
              </span>
            </span>
          </span>
          <span className="custom-panel__trigger-aside">
            <span className="custom-panel__trigger-swatches" aria-hidden="true">
              {[customization.primaryColor, customization.primaryColor1, customization.primaryColor2].map((color) => (
                <span key={color} style={{ '--swatch': color }} />
              ))}
            </span>
            <span className="custom-panel__chevron" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </span>
          </span>
        </button>

        <div
          id="custom-panel-content"
          className={`custom-panel__content ${isOpen ? 'is-open' : ''}`}
          role="region"
          aria-labelledby="custom-panel-heading"
        >
          <div className="custom-panel__body">
            <div className="custom-panel__main">
              <section className="custom-panel__section">
                <SectionTitle icon="spark" title="Quick Styles" meta="Preset combinations tuned for this portfolio." />
                <div className="custom-panel__preset-grid">
                  {stylePresets.map((preset) => {
                    const colors = [
                      preset.values.primaryColor,
                      preset.values.primaryColor1,
                      preset.values.primaryColor2,
                      preset.values.primaryColor4
                    ];

                    return (
                      <button
                        type="button"
                        key={preset.name}
                        className={`custom-panel__preset ${activePreset?.name === preset.name ? 'is-active' : ''}`}
                        onClick={() => applyPreset(preset)}
                        aria-pressed={activePreset?.name === preset.name}
                      >
                        <span className="custom-panel__preset-swatches" aria-hidden="true">
                          {colors.map((color) => (
                            <span key={color} style={{ '--swatch': color }} />
                          ))}
                        </span>
                        <span className="custom-panel__preset-name">{preset.name}</span>
                        <span className="custom-panel__preset-description">{preset.description}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="custom-panel__section">
                <SectionTitle icon="palette" title="Colors" meta="Fine-tune the accent system used across buttons, badges, and highlights." />
                <div className="custom-panel__color-grid">
                  {colorControls.map(({ key, label, note }) => (
                    <label key={key} className="custom-panel__color-card">
                      <span className="custom-panel__color-meta">
                        <span className="custom-panel__color-name">{label}</span>
                        <span className="custom-panel__color-note">{note}</span>
                      </span>
                      <span className="custom-panel__color-control">
                        <input
                          type="color"
                          value={customization[key]}
                          onChange={(event) => updateCustomization(key, event.target.value)}
                          aria-label={`${label} color`}
                        />
                        <span>{customization[key].toUpperCase()}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </section>

              <section className="custom-panel__section">
                <SectionTitle icon="type" title="Typography" meta="Choose the reading tone and scale." />
                <div className="custom-panel__font-grid">
                  {fonts.map((font) => (
                    <button
                      type="button"
                      key={font.value}
                      className={`custom-panel__font ${customization.fontFamily === font.value ? 'is-active' : ''}`}
                      style={{ fontFamily: font.value }}
                      onClick={() => updateCustomization('fontFamily', font.value)}
                      aria-pressed={customization.fontFamily === font.value}
                    >
                      <span>{font.name}</span>
                      <small>{font.sample}</small>
                    </button>
                  ))}
                </div>
                <OptionGroup
                  label="Text scale"
                  options={fontScaleOptions}
                  value={customization.fontScale}
                  onChange={(option) => updateCustomization('fontScale', option.value)}
                />
              </section>

              <section className="custom-panel__section">
                <SectionTitle icon="layout" title="Layout Feel" meta="Adjust spacing, width, shape, and surface depth." />
                <div className="custom-panel__group-grid">
                  <OptionGroup
                    label="Density"
                    options={densityOptions}
                    value={customization.density}
                    onChange={handleDensityChange}
                  />
                  <OptionGroup
                    label="Corners"
                    options={radiusOptions}
                    value={customization.borderRadius}
                    onChange={(option) => updateCustomization('borderRadius', option.value)}
                  />
                  <OptionGroup
                    label="Surfaces"
                    options={surfaceOptions}
                    value={customization.surfaceStyle}
                    onChange={(option) => updateCustomization('surfaceStyle', option.value)}
                  />
                  <OptionGroup
                    label="Depth"
                    options={depthOptions}
                    value={customization.visualDepth}
                    onChange={(option) => updateCustomization('visualDepth', option.value)}
                  />
                  <OptionGroup
                    label="Content width"
                    options={widthOptions}
                    value={customization.contentWidth}
                    onChange={(option) => updateCustomization('contentWidth', option.value)}
                  />
                </div>
              </section>

              <section className="custom-panel__section">
                <SectionTitle icon="motion" title="Motion & Access" meta="Set movement, cursor behavior, theme, and contrast." />
                <div className="custom-panel__group-grid">
                  <OptionGroup
                    label="Theme"
                    options={[
                      { name: 'Light', value: 'light', description: 'Bright canvas' },
                      { name: 'Dark', value: 'dark', description: 'Low-light canvas' }
                    ]}
                    value={theme}
                    onChange={(option) => setTheme(option.value)}
                  />
                  <OptionGroup
                    label="Motion"
                    options={motionOptions}
                    value={customization.motionMode}
                    onChange={handleMotionChange}
                  />
                  <OptionGroup
                    label="Cursor"
                    options={cursorOptions}
                    value={customization.cursorStyle}
                    onChange={(option) => updateCustomization('cursorStyle', option.value)}
                  />
                  <OptionGroup
                    label="Contrast"
                    options={contrastOptions}
                    value={customization.contrast}
                    onChange={(option) => updateCustomization('contrast', option.value)}
                  />
                </div>
              </section>
            </div>

            <aside className="custom-panel__preview" aria-label="Customization preview">
              <div className="custom-panel__preview-top">
                <span className="custom-panel__preview-dot" />
                <span className="custom-panel__preview-dot" />
                <span className="custom-panel__preview-dot" />
              </div>
              <div className="custom-panel__preview-card">
                <span className="custom-panel__preview-label">Live Preview</span>
                <h4>Fawaz N P</h4>
                <p>Software Developer crafting clean, useful web experiences.</p>
                <div className="custom-panel__preview-tags">
                  <span>React</span>
                  <span>UI</span>
                  <span>API</span>
                </div>
                <button type="button">View Project</button>
              </div>
              <dl className="custom-panel__summary">
                <div>
                  <dt>Font</dt>
                  <dd>{selectedFont.name}</dd>
                </div>
                <div>
                  <dt>Shape</dt>
                  <dd>{customization.borderRadius}</dd>
                </div>
                <div>
                  <dt>Motion</dt>
                  <dd>{customization.motionMode}</dd>
                </div>
              </dl>
              <button type="button" className="custom-panel__reset" onClick={resetCustomization}>
                <Icon type="reset" />
                Back to defaults
              </button>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomizationPanel;
